import React, { useEffect, useState } from 'react';
import EditIcon from '@mui/icons-material/DriveFileRenameOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/South';
import KeyboardArrowUpIcon from '@mui/icons-material/North';
import PlayIcon from '@mui/icons-material/PlayArrow';
import StorageIcon from '@mui/icons-material/Storage';
import CheckIcon from '@mui/icons-material/Check';

import { ContainedButton, OutlinedButton } from '../../modules/Buttons';
import { PermissionSelector } from '../../modules/PermissionSelector';
import { ACCESS_TYPE_ENUM } from '../../../enums/ACCESS_ENUM';
import { SortOptions, SortType } from '../../../enums/SORT';
import styles from './Filters.module.scss';
import Modal from '../../modules/Modal';

type TFiltersModal = {
  open: boolean;
  switchModal: () => void;
  setType: (s: any) => void;
  sortByPermission: (s: ACCESS_TYPE_ENUM[] | null) => void;
  onNameSort: (order: SortType) => void;
  onLastViewedSort: () => void;
  onLastEditSort: () => void;
  onSizeSort: () => void;
  sortOption: SortOptions | null;
  sortOrder: SortType;
  loading: boolean;
  type: 'folder' | 'composer' | 'all' | null;
  disableSortBySize: boolean;
};

export const FiltersModal: React.FC<TFiltersModal> = ({
  open,
  switchModal,
  setType,
  sortByPermission,
  onNameSort,
  onLastEditSort,
  onLastViewedSort,
  onSizeSort,
  sortOption,
  sortOrder,
  loading,
  type,
  disableSortBySize,
}) => {
  const [selectedPermissions, setSelectedPermissions] = useState<{ label: string; value: string }[]>([
    { label: 'All', value: ACCESS_TYPE_ENUM.ALL },
  ]);
  const [contentType, setContentType] = useState<{ label: string; value: string }[]>([
    { label: 'Folders', value: 'folder' },
    { label: 'Composers', value: 'composer' },
  ]);
  const onClose = () => {
    if (loading) return;
    switchModal();
  };

  useEffect(() => {
    if (type === 'composer') {
      setContentType([{ label: 'Composers', value: 'composer' }]);
    } else if (type === 'folder') {
      setContentType([{ label: 'Folders', value: 'folder' }]);
    } else {
      setContentType([
        { label: 'Folders', value: 'folder' },
        { label: 'Composers', value: 'composer' },
      ]);
    }
  }, [type]);

  const onPermissionSelect = (newPermissions: { label: string; value: string }[]) => {
    setSelectedPermissions(newPermissions);
    if (newPermissions.find((p) => p.value === ACCESS_TYPE_ENUM.ALL)) {
      sortByPermission(null);
    } else {
      // @ts-ignore
      sortByPermission(newPermissions.map((p) => p.value));
    }
  };

  const onContentTypeSelect = (newContentType: { label: string; value: string }[]) => {
    if (loading || !newContentType || newContentType[0]?.label === 'All') return;

    setContentType(newContentType);
    if (newContentType.length > 1) {
      setType('all');
    } else {
      setType(newContentType[0].value);
    }
  };

  const onRemovePermission = (permToFilter: string) => {
    const newPermissions = selectedPermissions.filter((v) => v.value !== permToFilter);
    if (newPermissions?.length) {
      setSelectedPermissions(newPermissions);
    } else {
      setSelectedPermissions([{ label: 'All', value: ACCESS_TYPE_ENUM.ALL }]);
    }
    // @ts-ignore
    sortByPermission(newPermissions ? newPermissions.map((p) => p.value) : null);
  };

  return (
    <Modal open={open} onClose={onClose} loading={loading}>
      <div className={styles.modalBody}>
        <div className={styles.modalTitle}>Select at least one sort or filter you want to apply </div>
        <div className={styles.modalSubtitle}>Sort</div>
        <div className={styles.subtitle}>Alphabetical</div>
        <div className={styles.sortOptionButtons}>
          <OutlinedButton
            kind={sortOption === SortOptions.NAME && sortOrder === SortType.ASC ? 'purple' : 'default'}
            className={styles.optionBtn}
            onClick={() => (loading ? '' : onNameSort(SortType.ASC))}
          >
            {' '}
            <div className={styles.leftSide}>
              <KeyboardArrowDownIcon style={{ fontSize: '15px', marginLeft: '4px', marginRight: '8px' }} />
              Ascending (A-Z)
            </div>
            <CheckIcon sx={{ height: '16px' }} />
          </OutlinedButton>
          <OutlinedButton
            kind={sortOption === SortOptions.NAME && sortOrder === SortType.DESC ? 'purple' : 'default'}
            className={styles.optionBtn}
            onClick={() => (loading ? '' : onNameSort(SortType.DESC))}
          >
            {' '}
            <div className={styles.leftSide}>
              <KeyboardArrowUpIcon style={{ fontSize: '15px', marginLeft: '4px', marginRight: '8px' }} />
              Descending (Z-A)
            </div>
            <CheckIcon sx={{ height: '16px' }} />
          </OutlinedButton>
        </div>
        <div className={styles.subtitle}>By date</div>
        <div className={styles.sortOptionButtons}>
          <OutlinedButton
            onClick={onLastViewedSort}
            kind={sortOption === SortOptions.LAST_VIEWED ? 'purple' : 'default'}
            className={styles.optionBtn}
          >
            {' '}
            <div className={styles.leftSide}>
              <PlayIcon style={{ fontSize: '15px', marginLeft: '4px', marginRight: '8px' }} />
              Last Viewed
            </div>
            {sortOption === SortOptions.LAST_VIEWED && sortOrder === SortType.ASC && <KeyboardArrowDownIcon style={{ fontSize: '15px' }} />}
            {sortOption === SortOptions.LAST_VIEWED && sortOrder === SortType.DESC && <KeyboardArrowUpIcon style={{ fontSize: '15px' }} />}
          </OutlinedButton>
          <OutlinedButton
            onClick={onLastEditSort}
            kind={sortOption === SortOptions.UPDATED_AT ? 'purple' : 'default'}
            className={styles.optionBtn}
          >
            {' '}
            <div className={styles.leftSide}>
              <EditIcon style={{ fontSize: '15px', marginLeft: '4px', marginRight: '8px' }} />
              Last Modified
            </div>
            {sortOption === SortOptions.UPDATED_AT && sortOrder === SortType.DESC && <KeyboardArrowDownIcon style={{ fontSize: '15px' }} />}
            {sortOption === SortOptions.UPDATED_AT && sortOrder === SortType.ASC && <KeyboardArrowUpIcon style={{ fontSize: '15px' }} />}
          </OutlinedButton>
        </div>

        {!disableSortBySize && (
          <>
            <div className={styles.subtitle}>By size</div>
            <div className={styles.sortOptionButtons}>
              <OutlinedButton
                onClick={onSizeSort}
                kind={sortOption === SortOptions.SIZE ? 'purple' : 'default'}
                className={styles.optionBtn}
              >
                {' '}
                <div className={styles.leftSide}>
                  <StorageIcon style={{ fontSize: '15px', marginLeft: '4px', marginRight: '8px' }} />
                  Size
                </div>
                {sortOption === SortOptions.SIZE && sortOrder === SortType.DESC && <KeyboardArrowDownIcon style={{ fontSize: '15px' }} />}
                {sortOption === SortOptions.SIZE && sortOrder === SortType.ASC && <KeyboardArrowUpIcon style={{ fontSize: '15px' }} />}
              </OutlinedButton>
            </div>
          </>
        )}

        <div className={styles.modalSubtitle}>Filter</div>
        <div className={styles.selectedFilters}>
          {contentType.map((c) => (
            <OutlinedButton kind="purple" className={styles.chip}>
              {' '}
              <div>{c.label}</div>
              <CheckIcon sx={{ height: '16px' }} />
            </OutlinedButton>
          ))}
          {selectedPermissions.map((p) => (
            <OutlinedButton onClick={() => onRemovePermission(p.value)} key={p.value} kind="purple" className={styles.chip}>
              {' '}
              <div>{p.label}</div>
              <CheckIcon sx={{ height: '16px' }} />
            </OutlinedButton>
          ))}
        </div>
        <div className={styles.filterOptionSelectors}>
          <div className={styles.selectorContainer}>
            <div className={styles.subtitle}> Content Type </div>
            <PermissionSelector
              title={'Content Type'}
              selectedValues={contentType}
              options={[
                { label: 'Folders', value: 'folder' },
                { label: 'Composers', value: 'composer' },
              ]}
              onSelect={onContentTypeSelect}
              style={{ left: '0px' }}
            />
          </div>
          <div className={styles.selectorContainer}>
            <div className={styles.subtitle}> Sharing Permissions </div>
            <PermissionSelector
              title={'Permissions'}
              selectedValues={selectedPermissions}
              options={[
                { label: 'All', value: ACCESS_TYPE_ENUM.ALL },
                { label: 'View', value: ACCESS_TYPE_ENUM.VIEW },
                { label: 'View & Comment', value: ACCESS_TYPE_ENUM.VIEW_AND_COMMENT },
                { label: 'Comment & Create', value: ACCESS_TYPE_ENUM.COMMENT_AND_CREATE },
                { label: 'Comment, Create & Reshare', value: ACCESS_TYPE_ENUM.COMMENT_CREATE_AND_RESHARE },
                { label: 'Edit & Reshare', value: ACCESS_TYPE_ENUM.EDIT_AND_RE_SHARE },
              ]}
              onSelect={onPermissionSelect}
            />
          </div>
        </div>
        <ContainedButton style={{ width: '100%', marginTop: '20px' }} onClick={onClose}>
          {' '}
          Close{' '}
        </ContainedButton>
      </div>
    </Modal>
  );
};
